import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";

interface Props {
  clubId: string;
}

function formatExpiry(d: Date | string) {
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function InvitePanel({ clubId }: Props) {
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const { t } = useTranslation();

  const utils = trpc.useUtils();
  const { data: pending } = trpc.invites.list.useQuery({ clubId });

  const create = trpc.invites.create.useMutation({
    onSuccess: () => {
      utils.invites.list.invalidate({ clubId });
      setEmail("");
    },
  });

  const createLink = trpc.invites.createLink.useMutation({
    onSuccess: (data) => {
      utils.invites.list.invalidate({ clubId });
      copyLink(data.token);
    },
  });

  const revoke = trpc.invites.revoke.useMutation({
    onSuccess: () => utils.invites.list.invalidate({ clubId }),
  });

  function inviteLink(token: string) {
    return `${window.location.origin}/invite/${token}`;
  }

  function copyLink(token: string) {
    navigator.clipboard.writeText(inviteLink(token));
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="space-y-4">
      {/* Email invite form */}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) create.mutate({ clubId, targetEmail: email.trim() });
        }}
      >
        <Input
          placeholder={t("invite.emailPlaceholder")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" size="sm" loading={create.isPending}>
          {t("invite.sendButton")}
        </Button>
      </form>

      {create.error && (
        <p className="text-xs" style={{ color: "var(--color-destructive)" }}>
          {create.error.message}
        </p>
      )}

      {/* Generate link-only invite */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
        <span className="text-xs flex-shrink-0" style={{ color: "var(--color-muted-foreground)" }}>
          {t("common.or")}
        </span>
        <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
      </div>

      <Button
        variant="secondary"
        size="sm"
        className="w-full"
        loading={createLink.isPending}
        onClick={() => createLink.mutate({ clubId })}
      >
        {t("invite.generateLink")}
      </Button>

      {/* Pending invites */}
      {pending && pending.length > 0 && (
        <ul className="space-y-2">
          {pending.map((inv) => (
            <li
              key={inv.token}
              className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm"
              style={{
                background: "var(--color-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <div className="min-w-0">
                <p className="truncate text-sm" style={{ color: "var(--color-foreground)" }}>
                  {inv.targetEmail ?? t("invite.linkOnly")}
                </p>
                <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                  {t("invite.expires", { date: formatExpiry(inv.expiresAt) })}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyLink(inv.token)}
                >
                  {copied === inv.token ? t("invite.copied") : t("invite.copyLink")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={revoke.isPending && revoke.variables?.token === inv.token}
                  onClick={() => revoke.mutate({ token: inv.token })}
                  style={{ color: "var(--color-destructive)" }}
                >
                  {t("invite.revoke")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pending?.length === 0 && (
        <p className="text-xs italic" style={{ color: "var(--color-muted-foreground)" }}>
          {t("invite.noInvites")}
        </p>
      )}
    </div>
  );
}
